import React, { useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { IconButton } from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { AddedFailureReason } from '@marageti/z4-sdk/lib/travel';
import SummaryCard from '../summary-card';
import AddFailureReasonDialog from '../../pages/trip-request/screen/add-failure-reason-dialog';
import CriteriaList from '../../pages/trip-request/screen/criteria-list';
import { FailureReasonChip, ScreeningCriteriaMap } from '../../pages/trip-request/screen/types';

const { OutlinedTrashIcon } = OutlinedIcons;

type FailureReasonsSummaryViewProps = {
  canEdit: boolean;
  isLoading: boolean;
  isSaving: boolean;
  isScreened: boolean;
  passedReasons: string[];
  reasons: FailureReasonChip[];
  screeningCriteriaMap?: ScreeningCriteriaMap;
  onAddFailureReasons: (drafts: AddedFailureReason[]) => Promise<boolean>;
  onRemoveFailureReason: (code: string) => void;
  onRemoveAllFailureReasons: () => void;
};

const FailureReasonsSummaryView = ({
  canEdit,
  isLoading,
  isSaving,
  isScreened,
  passedReasons,
  reasons,
  screeningCriteriaMap,
  onAddFailureReasons,
  onRemoveFailureReason,
  onRemoveAllFailureReasons,
}: FailureReasonsSummaryViewProps) => {
  const confirm = useConfirm();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const actionsDisabled = !canEdit || isSaving || isLoading;

  const handleRemoveAll = async () => {
    try {
      await confirm({
        title: 'Remove all Failure Reasons?',
        description: 'This action can not be undone.',
        confirmationText: 'Remove',
        cancellationText: 'Cancel',
        confirmationButtonProps: {
          color: 'primary',
          variant: 'contained',
        },
      });
      onRemoveAllFailureReasons();
    } catch {
      // Confirmation cancelled
    }
  };

  const handleAddFailureReasons = async (drafts: AddedFailureReason[]) => {
    const saved = await onAddFailureReasons(drafts);
    if (saved) {
      setIsAddDialogOpen(false);
    }
  };

  return (
    <>
      <SummaryCard
        addDisabled={actionsDisabled}
        extraActions={!isScreened && reasons.length > 0 && (
          <IconButton
            disabled={actionsDisabled}
            onClick={handleRemoveAll}
          >
            <OutlinedTrashIcon fontSize="small" color="primary" />
          </IconButton>
        )}
        isEmpty={reasons.length === 0}
        isLoading={isLoading}
        emptyMessage="There are no failure reasons for this trip."
        onAdd={isScreened ? undefined : () => setIsAddDialogOpen(true)}
        title="Failure Reasons"
      >
        <CriteriaList
          canEdit={canEdit}
          isCorrectionLoading={false}
          isSaving={isSaving}
          onRemove={onRemoveFailureReason}
          reasons={reasons}
          screeningCriteriaMap={screeningCriteriaMap}
        />
      </SummaryCard>
      <AddFailureReasonDialog
        excludedCodes={reasons.map((reason) => reason.code)}
        isSaving={isSaving}
        key={isAddDialogOpen ? 'open' : 'closed'}
        onClose={() => setIsAddDialogOpen(false)}
        onSubmit={handleAddFailureReasons}
        open={isAddDialogOpen}
        passedReasons={passedReasons}
        screeningCriteriaMap={screeningCriteriaMap}
      />
    </>
  );
};

export default FailureReasonsSummaryView;
